import {
  CaratPlanningDetails,
  CaratPosition,
} from '@/typings/models/CaratModel';
import { F } from '@/utils/intl';
import { Descriptions, Empty, Flex, Table, Tag, Typography } from 'antd';
import React from 'react';
import { useIntl } from 'umi';
import styles from './CaratPlanViewer.less';
import {
  CARAT_SIDES,
  useCaratAttributeColumns,
  useCaratPositionColumns,
} from './constants';
import {
  CARAT_EMPTY,
  CARAT_TRADE_GROUP_COLORS,
  CaratFormatter,
} from './format';

const { Text } = Typography;

export interface CaratPlanPositionsProps {
  details?: CaratPlanningDetails;
  format: CaratFormatter;
}

/** A position's key: Carat's position numbers are unique within one export. */
const positionKey = (position: CaratPosition, index: number) =>
  position.position || position.article_number || `position-${index}`;

/**
 * The positions of a Carat planning, in the groups Carat itself files them
 * under ("Möbelteile", "Einbaugeräte", "Küchenarmaturen", …) with each group's
 * subtotal, followed by the rows Carat left ungrouped.
 *
 * Expanding a row shows what does not fit a column: the catalogue it is priced
 * from, its decor, its EAN, the VAT and cost behind the gross price, and the
 * purchase order it went out on.
 */
const CaratPlanPositions: React.FC<CaratPlanPositionsProps> = ({
  details,
  format,
}) => {
  const intl = useIntl();
  const columns = useCaratPositionColumns(format);
  const attributeColumns = useCaratAttributeColumns();

  const positions = details?.positions ?? [];
  const groups = details?.groups ?? [];

  if (positions.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const ungrouped = positions.filter((position) => !position.group);

  const sections = groups.map((group) => ({
    key: `${group.trade_group ?? ''}-${group.number ?? ''}`,
    label: group.label || group.number || CARAT_EMPTY,
    tradeGroup: group.trade_group,
    total: group.total_gross,
    positions: positions.filter(
      (position) =>
        position.group?.trade_group === group.trade_group &&
        position.group?.number === group.number,
    ),
  }));

  if (ungrouped.length > 0) {
    sections.push({
      key: 'ungrouped',
      label: intl.formatMessage({
        id: 'pages.integrations.carat.ungrouped',
        defaultMessage: 'Without a group',
      }),
      tradeGroup: null,
      total: null,
      positions: ungrouped,
    });
  }

  const expandedRow = (position: CaratPosition) => (
    <Flex vertical gap={12} className={styles.positionDetail}>
      <Descriptions size="small" column={{ xs: 1, md: 2, xl: 3 }} colon={false}>
        <Descriptions.Item
          label={
            <F
              id="pages.integrations.carat.catalog"
              defaultMessage="Catalogue"
            />
          }
        >
          {[position.catalog_name, position.series]
            .filter(Boolean)
            .join(' – ') || CARAT_EMPTY}
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <F
              id="pages.integrations.carat.supplier_item"
              defaultMessage="Supplier item"
            />
          }
        >
          {format.text(position.supplier_item)}
        </Descriptions.Item>
        <Descriptions.Item
          label={<F id="pages.integrations.carat.ean" defaultMessage="EAN" />}
        >
          {format.text(position.ean)}
        </Descriptions.Item>
        <Descriptions.Item
          label={<F id="pages.integrations.carat.side" defaultMessage="Side" />}
        >
          {position.side && CARAT_SIDES[position.side] ? (
            <F {...CARAT_SIDES[position.side]} />
          ) : (
            format.text(position.side)
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={<F id="pages.integrations.carat.net" defaultMessage="Net" />}
        >
          {format.money(position.price?.net)}
        </Descriptions.Item>
        <Descriptions.Item
          label={<F id="pages.integrations.carat.vat" defaultMessage="VAT" />}
        >
          {format.money(position.price?.vat)}
          {position.price?.vat_rate != null &&
            ` (${format.percent(position.price.vat_rate)})`}
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <F
              id="pages.integrations.carat.cost"
              defaultMessage="Purchase cost"
            />
          }
        >
          {format.money(position.price?.cost)}
        </Descriptions.Item>
        {position.order?.number && (
          <Descriptions.Item
            label={
              <F
                id="pages.integrations.carat.order_number"
                defaultMessage="Order no."
              />
            }
          >
            {position.order.number}
          </Descriptions.Item>
        )}
        {position.order?.delivery_on && (
          <Descriptions.Item
            label={
              <F
                id="pages.integrations.carat.delivery_date"
                defaultMessage="Delivery date"
              />
            }
          >
            {format.date(position.order.delivery_on)}
          </Descriptions.Item>
        )}
      </Descriptions>

      {(position.finishes?.length ?? 0) > 0 && (
        <Table
          size="small"
          rowKey={(finish) => `${finish.code}-${finish.value_code}`}
          dataSource={position.finishes}
          columns={attributeColumns}
          pagination={false}
          title={() => (
            <Text strong>
              <F
                id="pages.integrations.carat.finishes"
                defaultMessage="Decor"
              />
            </Text>
          )}
        />
      )}

      {(position.adjustments?.length ?? 0) > 0 && (
        <div>
          <Text strong>
            <F
              id="pages.integrations.carat.adjustments"
              defaultMessage="Price reductions"
            />
          </Text>
          <Flex gap={8} wrap className={styles.adjustments}>
            {position.adjustments?.map((adjustment, index) => (
              <Tag key={`${adjustment.label}-${index}`}>
                {adjustment.label}: {format.signedMoney(adjustment.amount)}
              </Tag>
            ))}
          </Flex>
        </div>
      )}
    </Flex>
  );

  return (
    <Flex vertical gap={24}>
      {sections.map((section) => (
        <Table<CaratPosition>
          key={section.key}
          size="small"
          rowKey={positionKey}
          dataSource={section.positions}
          columns={columns}
          pagination={false}
          expandable={{ expandedRowRender: expandedRow }}
          title={() => (
            <Flex align="center" gap={8}>
              {section.tradeGroup && (
                <Tag color={CARAT_TRADE_GROUP_COLORS[section.tradeGroup]}>
                  {section.tradeGroup}
                </Tag>
              )}
              <Text strong>{section.label}</Text>
              <Text type="secondary">({section.positions.length})</Text>
            </Flex>
          )}
          summary={() =>
            section.total == null ? null : (
              <Table.Summary.Row>
                {/* The expandable column counts: 1 + 7 columns before the total. */}
                <Table.Summary.Cell index={0} colSpan={8}>
                  <Text strong>
                    <F id="pages.billings.subtotal" defaultMessage="Subtotal" />
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8} align="right">
                  <Text strong>{format.money(section.total)}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )
          }
        />
      ))}
    </Flex>
  );
};

export default CaratPlanPositions;
